import { clinicalPacksQueryOptions } from '@/hooks/use-clinical-packs';
import { EventProperties } from '@calm/evexia-analytics';
import {
    MediaProgramGuide,
    MediaUpdateGuideProgressActionEnum,
    WorkflowStepDataContentPack,
    WorkflowStepInfo,
} from '@calm/evexia-web-sdk';
import { AudioPlayerOpenState } from '@rhg/components/media/audio-player-combined/types';
import { AUDIO_PLAYER_DRAWER_HEIGHT } from '@rhg/components/media/audio-player-drawer';
import { useActiveCircle } from '@rhg/hooks/use-active-circle';
import { useAudio } from '@rhg/hooks/use-audio';
import { useCurrentBreakpointName } from '@rhg/hooks/use-current-breakpoint-name';
import { MediaTrackerFn } from '@rhg/hooks/use-media-tracker/types';
import { useProgramContext } from '@rhg/hooks/use-program-context';
import { ProgramContextProps } from '@rhg/hooks/use-program-context/types';
import { programsQueryOptions } from '@rhg/hooks/use-programs';
import { useRecentAndFavoriteGuides } from '@rhg/hooks/use-recent-and-favorite-guides';
import { useForceRefreshCircleRecommendations } from '@rhg/hooks/use-recommendations';
import { useUpdateGuide } from '@rhg/hooks/use-update-guide';
import { useUpdateWorkflowStep } from '@rhg/hooks/use-update-workflow-step';
import { useFetchWorkflowNextStep } from '@rhg/hooks/use-workflow-next-step';
import { invalidateQueryKey } from '@rhg/libs/react-query';
import { getLocalDateTimeString } from '@rhg/utils/date-time';
import { mod } from '@rhg/utils/math';
import { MediaEvent } from '@rhg/utils/media';
import dynamic from 'next/dynamic';
import {
    createContext,
    ReactNode,
    useCallback,
    useMemo,
    useState,
} from 'react';
import { ActiveProgramProvider } from '../program';
import { GuidePlayerState, OpenGuidePlayerProps } from './types';

const GuidePlayer = dynamic(() =>
    import('@rhg/components/guide/player').then((mod) => mod.GuidePlayer)
);
const GuideCompletedDialogs = dynamic(() =>
    import('@rhg/components/guide/completed-dialogs').then(
        (mod) => mod.GuideCompletedDialogs
    )
);

export enum ContentType {
    Soundscape,
    Playlist,
    Unspecified,
}

export enum TrackToPlay {
    Previous,
    Next,
}

const getGuideContentType = (contentType?: string): ContentType => {
    const formattedType = contentType?.toLowerCase();
    if (formattedType === 'soundscape') {
        return ContentType.Soundscape;
    }

    if (formattedType === 'sequential' || formattedType === 'music') {
        return ContentType.Playlist;
    }

    return ContentType.Unspecified;
};

export const GuidePlayerContext = createContext<GuidePlayerState>(null!);

function usePlayerHeight(openState: AudioPlayerOpenState) {
    const currentBreakpoint = useCurrentBreakpointName();
    if (!openState) {
        return 0;
    }

    return AUDIO_PLAYER_DRAWER_HEIGHT[currentBreakpoint];
}

interface Props {
    children: ReactNode;
}

export const GUIDE_PLAYER_OPEN_STATE_KEY = 'guide_player_open_state';

export const GuidePlayerProvider = ({ children }: Props) => {
    const { activeCircle, activeCircleId } = useActiveCircle();
    const { updateWorkflowStepAsync } = useUpdateWorkflowStep();
    const { fetchWorkflowNextStepAsync } = useFetchWorkflowNextStep();
    const { unloadAudio } = useAudio();
    const { forceRefreshRecommendations } =
        useForceRefreshCircleRecommendations();
    const [openState, setOpenState] = useState<AudioPlayerOpenState>(false);
    const [isGuideCompletedOpen, setIsGuideCompletedOpen] = useState(false);
    const [completedContentStep, setCompletedContentStep] = useState<
        WorkflowStepInfo | undefined
    >(undefined);
    const [playingGuide, setPlayingGuide] = useState<MediaProgramGuide | null>(
        null
    );
    const [trackFn, setTrackFn] = useState<MediaTrackerFn | undefined>(
        undefined
    );
    const [contentType, setContentType] = useState<ContentType>();
    const [willSkipToPreviousProgress, setWillSkipToPreviousProgress] =
        useState(false);
    const [sessionStartTrackProps, setSessionStartTrackProps] =
        useState<EventProperties>({});
    const playerHeight = usePlayerHeight(openState);

    const [programContextProps, setProgramContextProps] =
        useState<ProgramContextProps>();
    const programContext = useProgramContext(programContextProps ?? {});
    const { updateGuideAsync } = useUpdateGuide();
    const { refresh: refreshRecentsAndFavorites } = useRecentAndFavoriteGuides(
        activeCircleId,
        false
    );

    // Set guide and open first
    const closeGuidePlayer = useCallback(
        (isTriggeredFromTrackChange = false) => {
            // eslint-disable-next-line no-restricted-syntax
            localStorage.removeItem(GUIDE_PLAYER_OPEN_STATE_KEY);
            if (!isTriggeredFromTrackChange) {
                setOpenState(false);
            }
            setTrackFn(() => undefined);
            if (!isTriggeredFromTrackChange) {
                setPlayingGuide(null);
            }
            unloadAudio();
            if (!isTriggeredFromTrackChange) {
                setProgramContextProps(undefined);
            }
            setIsGuideCompletedOpen(false);
            setCompletedContentStep(undefined);
            setContentType(undefined);
        },
        [unloadAudio]
    );

    // Set guide and then open last
    const openGuidePlayer = useCallback(
        async ({
            openState,
            guide,
            packClass,
            workflow,
            program,
            trackFn,
            willSkipToPreviousProgress,
            sessionStartTrackProps,
        }: OpenGuidePlayerProps) => {
            const newGuideIsCurrentlyPlaying = guide.id === playingGuide?.id;
            if (!newGuideIsCurrentlyPlaying) {
                // First clear out any existing player data, if new guide is being opened
                closeGuidePlayer();
            }

            setProgramContextProps({ packClass, program, workflow });
            setTrackFn(() => trackFn);
            setSessionStartTrackProps(sessionStartTrackProps ?? {});
            setPlayingGuide(guide);
            setWillSkipToPreviousProgress(willSkipToPreviousProgress ?? false);

            setContentType(getGuideContentType(program.meditationType));
            const newState = openState ?? 'dialog';
            setOpenState(newState);
            // eslint-disable-next-line no-restricted-syntax
            localStorage.setItem(
                GUIDE_PLAYER_OPEN_STATE_KEY,
                newState.toString()
            );
            if (!newGuideIsCurrentlyPlaying) {
                await updateGuideAsync({
                    programId: guide.programId,
                    guideId: guide.id,
                    packClass: packClass,
                    workflowDefnId: workflow?.workflowDefn?.defnId,
                    workflowType: workflow?.workflowDefn?.type,
                    request: {
                        progressDurationMs: 0,
                        timestamp: getLocalDateTimeString(),
                        progressAction:
                            MediaUpdateGuideProgressActionEnum.Start,
                    },
                });
            }
            refreshRecentsAndFavorites();
        },
        [
            playingGuide?.id,
            closeGuidePlayer,
            updateGuideAsync,
            refreshRecentsAndFavorites,
        ]
    );

    const sessionsAndCurrentSessionIndex = useMemo(() => {
        const sessions = programContext.orderedSessions.filter(
            ({ isTrailer }) => !isTrailer
        );
        const currentSessionIndex = sessions.findIndex(
            ({ id }) => id === playingGuide?.id
        );
        return { sessions, currentSessionIndex };
    }, [playingGuide?.id, programContext.orderedSessions]);

    const onTrackChange = useCallback(
        (trackToPlay: TrackToPlay) => {
            const { sessions, currentSessionIndex } =
                sessionsAndCurrentSessionIndex;

            const newTrackIndex =
                trackToPlay === TrackToPlay.Next
                    ? mod(currentSessionIndex + 1, sessions.length)
                    : mod(currentSessionIndex - 1, sessions.length);

            if (
                currentSessionIndex < 0 ||
                sessions.length === 0 ||
                !programContext.packClass ||
                !programContext.program
            ) {
                return;
            }

            closeGuidePlayer(true);
            openGuidePlayer({
                openState,
                guide: sessions[newTrackIndex],
                packClass: programContext.packClass,
                program: programContext.program,
                tools: programContext.toolsByGuide[sessions[newTrackIndex].id],
                trackFn,
                willSkipToPreviousProgress: false,
            });
        },
        [
            sessionsAndCurrentSessionIndex,
            closeGuidePlayer,
            openGuidePlayer,
            openState,
            programContext.packClass,
            programContext.program,
            programContext.toolsByGuide,
            trackFn,
        ]
    );

    const isPlaylistWithNextTrack = useMemo(() => {
        if (contentType !== ContentType.Playlist) return false;

        const { sessions, currentSessionIndex } =
            sessionsAndCurrentSessionIndex;

        const hasNextTrack =
            currentSessionIndex >= 0 &&
            currentSessionIndex < sessions.length - 1;
        return hasNextTrack;
    }, [contentType, sessionsAndCurrentSessionIndex]);

    const onProgress = useCallback<MediaTrackerFn>(
        async (event) => {
            if (
                event.eventType === MediaEvent.Ended &&
                !isPlaylistWithNextTrack
            ) {
                setOpenState(false);
                setTrackFn(() => undefined);
                setCompletedContentStep(undefined);
                setIsGuideCompletedOpen(false);

                // Don't do post session if the guide is a trailer
                if (
                    playingGuide?.isTrailer ||
                    programContext?.programType !== 'clinical_program'
                ) {
                    closeGuidePlayer();
                } else {
                    const everySessionIsATrailerCompletedOrCurrentGuide =
                        programContext?.orderedSessions.every(
                            (session) =>
                                session.isTrailer ||
                                session.completed ||
                                session.id === playingGuide?.id
                        );
                    // If all sessions are complete then update the content workflow then show the guide completed dialogs
                    if (
                        everySessionIsATrailerCompletedOrCurrentGuide &&
                        programContext?.currentWorkflowStep?.stepDefn.type ===
                            'content_pack'
                    ) {
                        const stepData = programContext?.currentWorkflowStep
                            ?.stepDefn?.data as WorkflowStepDataContentPack;
                        const options = {
                            onSuccess: () => {
                                forceRefreshRecommendations(activeCircle);
                                setCompletedContentStep(
                                    programContext?.currentWorkflowStep
                                );
                                setIsGuideCompletedOpen(true);
                            },
                        };
                        // If the step is a client state update then update the workflow step manually
                        if (stepData.clientStateUpdate) {
                            await updateWorkflowStepAsync({
                                workflowDefnId:
                                    programContext?.workflow?.workflowDefn
                                        ?.defnId,
                                stepDefnId:
                                    programContext?.currentWorkflowStep
                                        ?.stepDefn?.defnId,
                                workflowType:
                                    programContext?.workflow?.workflowDefn.type,
                                request: { state: 'step_completed' },
                            });
                            options.onSuccess();
                            // Otherwise just fetch the next step
                        } else {
                            await fetchWorkflowNextStepAsync({
                                workflowDefnId:
                                    programContext?.workflow?.workflowDefn
                                        .defnId,
                                workflowType:
                                    programContext?.workflow?.workflowDefn.type,
                                circle: activeCircle!,
                            });
                            options.onSuccess();
                        }
                        // Otherwise just show the guide completed dialogs
                    } else {
                        setIsGuideCompletedOpen(true);
                    }

                    if (
                        programContext?.program?.id &&
                        programContext?.packClass &&
                        activeCircleId
                    ) {
                        await invalidateQueryKey(
                            programsQueryOptions.queryKey({
                                packClassesWithProgramIdsMap: {
                                    [programContext.packClass]: new Set([
                                        programContext.program.id,
                                    ]),
                                },
                                circleId: activeCircleId,
                            })
                        );
                    }
                }
            }
            trackFn?.(event);
        },
        [
            isPlaylistWithNextTrack,
            activeCircleId,
            activeCircle,
            closeGuidePlayer,
            fetchWorkflowNextStepAsync,
            forceRefreshRecommendations,
            playingGuide?.id,
            playingGuide?.isTrailer,
            programContext?.packClass,
            programContext?.program?.id,
            programContext?.programType,
            programContext?.orderedSessions,
            programContext?.currentWorkflowStep,
            programContext?.workflow,
            trackFn,
            updateWorkflowStepAsync,
        ]
    );

    const onFavoriteSuccess = useCallback(
        (isFavorite: boolean) => {
            const { programType, packClass } = programContext;

            if (programType === 'clinical_program' && packClass) {
                invalidateQueryKey(
                    clinicalPacksQueryOptions.queryKey({
                        packClasses: [packClass],
                        circleId: activeCircleId,
                    })
                );
            }

            setPlayingGuide((prevGuide) =>
                prevGuide ? { ...prevGuide, favorite: isFavorite } : null
            );
        },
        [activeCircleId, programContext]
    );

    const guidePlayerValue = useMemo(
        () => ({
            closeGuidePlayer,
            onFavoriteSuccess,
            openGuidePlayer,
            openState,
            playerHeight,
            playingGuide,
        }),
        [
            closeGuidePlayer,
            onFavoriteSuccess,
            openGuidePlayer,
            openState,
            playerHeight,
            playingGuide,
        ]
    );

    return (
        <GuidePlayerContext.Provider value={guidePlayerValue}>
            {children}
            <ActiveProgramProvider {...programContextProps}>
                {isGuideCompletedOpen && playingGuide && (
                    <GuideCompletedDialogs
                        isOpen={isGuideCompletedOpen}
                        onClose={closeGuidePlayer}
                        openGuidePlayer={openGuidePlayer}
                        guideId={playingGuide.id}
                        completedContentStep={completedContentStep}
                    />
                )}
                {openState && playingGuide && programContext?.packClass && (
                    <GuidePlayer
                        openState={openState}
                        setOpenState={setOpenState}
                        guide={playingGuide}
                        context={programContext}
                        willSkipToPreviousProgress={willSkipToPreviousProgress}
                        onClose={closeGuidePlayer}
                        onProgress={onProgress}
                        onTrackChange={onTrackChange}
                        onFavoriteSuccess={onFavoriteSuccess}
                        contentType={contentType}
                        tools={programContext?.toolsByGuide[playingGuide.id]}
                        sessionStartTrackProps={sessionStartTrackProps}
                    />
                )}
            </ActiveProgramProvider>
        </GuidePlayerContext.Provider>
    );
};
